import { Button, Select } from '@round/ui-kit';
import { StylesConfig, ValueType } from 'react-select';
import { useCallback, useRef, useState } from 'react';
import { getTiktokHashTags, getTiktokHashtagReportsFile } from '@round/api';
import styles from './HashtagReportDownload.module.css';
import { useSelectPaginatedOptions } from '../../../../../Hooks/useSelectPaginatedOptions';
import { GenericDropdownOption } from '../../../../../App.types';
import useUrlState from '../../../../../Hooks/useUrlState';
import { showNotification } from '../../../../../helpers';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { downloadFile, formatToIsoDateString } from '../../../../../utility/utility';
import DatePickerSelect from '../../../../../ui/DataEntry/DatePicker/DatePickerSelect/DatePickerSelect';
import useHashtagPaste, { HashtagOption, mergeHashtagOptions } from '../useHashtagPaste';

const selectStyles: StylesConfig = {
    control: (provided) => ({
        ...provided,
        maxWidth: '30rem',
    }),
};

type UrlState = {
    selectedHashtagIds: string;
    start_date?: string;
};

const HashtagReportDownload = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const [selectedHashtagOptions, setSelectedHashtagOptions] = useState<
        ValueType<GenericDropdownOption<number>, true>
    >([]);

    const [isDownloading, setIsDownloading] = useState(false);

    const setSelection = useCallback(
        (options: ValueType<HashtagOption, true>) => {
            setSelectedHashtagOptions(options);
            setUrlState({ selectedHashtagIds: options?.map((option) => option.value).join() });
        },
        [setUrlState]
    );

    // What the lookup merges into, so a tag picked while it was in flight survives the paste.
    const selectionRef = useRef(selectedHashtagOptions);
    selectionRef.current = selectedHashtagOptions;

    const { onPaste, isResolving } = useHashtagPaste({
        onResolved: (resolved) => setSelection(mergeHashtagOptions(selectionRef.current ?? [], resolved)),
    });

    const { props: hashtagSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getTiktokHashTags
    >({
        fetchOptions: getTiktokHashTags,
        mapToOption: (hashtag) => ({
            label: `#${hashtag.title}`,
            value: hashtag.id,
        }),
    });

    const initSelectedOptions = useCallback(async (hashtagIds: number[], requestInit?: RequestInit) => {
        try {
            const hashtagsResponse = await getTiktokHashTags(
                { id: hashtagIds.join(','), page_size: hashtagIds.length },
                requestInit
            );

            const options = hashtagsResponse.data.results.map((hashtag) => ({
                label: `#${hashtag.title}`,
                value: hashtag.id,
            }));
            setSelectedHashtagOptions(options);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification((e as Error).message, 'error');
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            const hashtagIds =
                urlState.selectedHashtagIds
                    ?.split(',')
                    .filter((item) => !!item)
                    .map((item) => Number(item)) ?? [];

            const hashtagsToInit = hashtagIds.filter(
                (id) => !selectedHashtagOptions?.find((option) => option.value === id)
            );
            if (hashtagsToInit.length) {
                initSelectedOptions(hashtagsToInit, { signal });
            }
        },
        [urlState.selectedHashtagIds, selectedHashtagOptions, initSelectedOptions]
    );

    const selectedStartDate: Date | null = urlState.start_date ? new Date(urlState.start_date) : null;

    const handleSetStartDate = (date: Date | null) => {
        if (!date) {
            setUrlState({ start_date: undefined });
            return;
        }

        setUrlState({
            start_date: formatToIsoDateString(date),
        });
    };

    const onFileDownload = useCallback(async (hashtagIds: string | undefined, startDate: string | undefined) => {
        if (!hashtagIds) {
            return;
        }

        try {
            setIsDownloading(true);
            const response = await getTiktokHashtagReportsFile({ hashtag_ids: hashtagIds, start_date: startDate });

            if (response.status === 200) {
                downloadFile(response.data);
                return;
            }
            throw new Error('Error downloading file');
        } catch (e) {
            showNotification((e as Error).message, 'error');
        } finally {
            setIsDownloading(false);
        }
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.column}>
                <span className={styles.label}>Hashtags</span>
                {/* Paste lands on the select's own input, and the event reaches this handler on the way up. */}
                <div onPaste={onPaste}>
                    <Select
                        {...hashtagSelectProps}
                        value={selectedHashtagOptions}
                        onChange={setSelection}
                        isLoading={hashtagSelectProps.isLoading || isResolving}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isMulti
                        filterOption={null}
                        placeholder="Paste or search for hashtags..."
                    />
                </div>
            </div>
            <div className={styles.column}>
                <span className={styles.label}>Start Date</span>
                <DatePickerSelect
                    value={selectedStartDate}
                    onChange={handleSetStartDate}
                    onClear={() => {
                        handleSetStartDate(null);
                    }}
                    className={styles.datePickerFilter}
                    maxDate={new Date()}
                />
            </div>

            <div>
                <Button
                    type="filled"
                    color="black"
                    disabled={!urlState.selectedHashtagIds?.length}
                    loading={isDownloading}
                    onClick={() => onFileDownload(urlState.selectedHashtagIds, urlState.start_date)}
                >
                    Download
                </Button>
            </div>
        </div>
    );
};

export default HashtagReportDownload;
